(function () {
  if (window.MskCampaignBlocks) return window.MskCampaignBlocks.initAll();

  const requests = new Map();
  const css = "msk-active-campaign__";
  const heroImageWidths = [640, 960, 1200, 1600, 2200, 2600];
  const bottleVisibleBounds = new Map();
  let productCarouselId = 0;

  function text(value) { return String(value == null ? "" : value).trim(); }

  function clamp(value, fallback, minimum, maximum) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.min(maximum, Math.max(minimum, parsed)) : fallback;
  }

  function element(tagName, className) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    return node;
  }

  function addText(parent, tagName, value, className) {
    const clean = text(value);
    if (!clean) return null;
    const node = element(tagName, className);
    node.textContent = clean;
    parent.appendChild(node);
    return node;
  }

  function campaignCartLoadingSpinner(root) {
    const homepage = root?.closest?.(".msk-campaign-homepage");
    const source = homepage?.querySelector?.("template[data-msk-campaign-loading-spinner]")
      ?.content?.firstElementChild;
    return source?.cloneNode ? source.cloneNode(true) : element("div", "loading__spinner hidden");
  }

  function appendLink(parent, label, href, className) {
    const cleanLabel = text(label);
    const cleanHref = text(href);
    if (!cleanLabel || !cleanHref) return null;
    const link = element("a", className);
    link.href = cleanHref;
    link.textContent = cleanLabel;
    parent.appendChild(link);
    return link;
  }

  function resizedShopifyImageUrl(value, width, quality = 75) {
    const source = text(value);
    if (!source) return "";
    try {
      const url = new URL(source, window.location.origin);
      if (/\/cdn\/shop\/(files|products)\//.test(url.pathname)
        || (url.hostname === "cdn.shopify.com" && /^\/s\/files\/.+\/(files|products)\//.test(url.pathname))) {
        url.searchParams.set("width", String(width));
        if (quality !== null) url.searchParams.set("quality", String(quality));
      }
      return url.toString();
    } catch {
      return source;
    }
  }

  function prepareResourceImage(image, source, sizes) {
    image.loading = "lazy";
    image.decoding = "async";
    if (resizedShopifyImageUrl(source, 280, null) !== resizedShopifyImageUrl(source, 1200, null)) {
      image.sizes = sizes;
      image.srcset = [280, 360, 480, 600, 800, 960, 1200]
        .map((width) => `${resizedShopifyImageUrl(source, width, null)} ${width}w`)
        .join(", ");
    }
    image.src = resizedShopifyImageUrl(source, 960, null);
  }

  function prepareHeroImage(image, source, primary = true) {
    image.decoding = "async";
    image.fetchPriority = primary ? "high" : "low";
    image.loading = "eager";
    image.sizes = "100vw";
    image.srcset = heroImageWidths
      .map((width) => `${resizedShopifyImageUrl(source, width)} ${width}w`)
      .join(", ");
    image.src = resizedShopifyImageUrl(source, 2200);
  }

  function addMobileSource(picture, url) {
    const source = element('source');
    source.media = '(max-width: 640px)';
    source.sizes = '100vw';
    source.srcset = [390, 640, 960, 1200].map(width => `${resizedShopifyImageUrl(url, width)} ${width}w`).join(', ');
    picture.appendChild(source);
  }

  function setResponsiveBackdrop(section, item) {
    const desktop = text(item.image_url || item.mobile_image_url);
    section.style.setProperty('--campaign-backdrop', `url(${JSON.stringify(desktop)})`);
    section.style.setProperty('--campaign-mobile-backdrop', `url(${JSON.stringify(text(item.mobile_image_url) || desktop)})`);
  }

  function setResponsiveImageCrop(section, item) {
    // The storefront also accepts preview form strings; only bounded numbers reach CSS.
    const crop = value => {
      if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return null; } }
      if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
      const number = (key, fallback, min, max) => {
        const n = value[key] == null || value[key] === '' ? NaN : Number(value[key]);
        return Number.isFinite(n) ? Math.round(Math.min(max, Math.max(min, n))) : fallback;
      };
      return { zoom: number('zoom', 100, 100, 300), x: number('x', 50, 0, 100), y: number('y', 50, 0, 100) };
    };
    const fallback = { zoom: 100, x: 50, y: 50 };
    const desktop = crop(item.image_crop) || (!item.image_url ? crop(item.mobile_image_crop) : null) || fallback;
    const mobile = crop(item.mobile_image_crop) || (item.mobile_image_url ? fallback : desktop);
    for (const [device, value] of [['desktop', desktop], ['mobile', mobile]]) {
      section.style.setProperty('--campaign-' + device + '-crop-zoom', String(value.zoom / 100));
      section.style.setProperty('--campaign-' + device + '-crop-x', value.x + '%');
      section.style.setProperty('--campaign-' + device + '-crop-y', value.y + '%');
    }
  }

  function appendPhotoCredit(parent, item) {
    const desktop = text(item.image_url ? item.image_credit : item.mobile_image_url ? item.mobile_image_credit : '');
    const mobile = text(item.mobile_image_url ? item.mobile_image_credit : desktop);
    if (!desktop && !mobile) return;
    const credit = element('div', css + 'photo-credit');
    for (const [device, value] of [['desktop', desktop], ['mobile', mobile]]) {
      const full = value.replace(/^foto\s*:\s*/i, '').trim();
      if (!full) continue;
      const author = full.split(/\s+(?:[—–·]|\|)\s+/, 1)[0];
      const deviceClass = css + 'photo-credit--' + device;
      if (author === full) {
        addText(credit, 'span', 'Foto: ' + author, deviceClass);
        continue;
      }
      const details = element('details', deviceClass);
      addText(details, 'summary', 'Foto: ' + author);
      const info = element('div', css + 'photo-credit-info');
      const sourceUrl = full.match(/(?:^|\s)(https:\/\/[^\s<>]+)\s*$/)?.[1];
      info.textContent = 'Foto: ' + (sourceUrl ? full.slice(0, full.lastIndexOf(sourceUrl)).replace(/\s*[·|]\s*$/, '').trimEnd() : full);
      if (sourceUrl) {
        const link = element('a');
        link.href = sourceUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = sourceUrl;
        info.appendChild(link);
      }
      details.appendChild(info);
      credit.appendChild(details);
    }
    parent.appendChild(credit);
  }

  function imageTasteBoxId(value) {
    const id = text(value).replace(/^gid:\/\/shopify\/Product\//, '');
    return /^\d+$/.test(id) ? id : '';
  }

  function measureVisibleBottle(source) {
    const url = text(source);
    if (!url || typeof document === 'undefined' || typeof Image !== 'function') {
      return Promise.resolve(null);
    }
    if (bottleVisibleBounds.has(url)) return bottleVisibleBounds.get(url);
    const measurement = (async () => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.src = url;
      await probe.decode();
      const ratio = Math.min(1, 220 / Math.max(probe.naturalWidth, probe.naturalHeight));
      const width = Math.max(1, Math.round(probe.naturalWidth * ratio));
      const height = Math.max(1, Math.round(probe.naturalHeight * ratio));
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(probe, 0, 0, width, height);
      const pixels = context.getImageData(0, 0, width, height).data;
      let left = width, right = -1, top = height, bottom = -1;
      for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
        if (pixels[(y * width + x) * 4 + 3] < 24) continue;
        left = Math.min(left, x); right = Math.max(right, x);
        top = Math.min(top, y); bottom = Math.max(bottom, y);
      }
      if (right < left || bottom < top) return null;
      return {
        centerX: ((left + right + 1) / 2) / width,
        centerY: ((top + bottom + 1) / 2) / height,
        width: (right - left + 1) / width,
        height: (bottom - top + 1) / height,
      };
    })().catch(() => null);
    bottleVisibleBounds.set(url, measurement);
    return measurement;
  }

  function bottleResources(resources, tasteBoxId = '') {
    const source = Array.isArray(resources) ? resources : [];
    const selectedTasteBoxId = imageTasteBoxId(tasteBoxId);
    const selectedTasteBox = selectedTasteBoxId
      ? source.find(resource => resource?.type === 'taste_box' && String(resource.id) === selectedTasteBoxId)
      : null;
    const bottleSource = selectedTasteBox ? [selectedTasteBox] : source;
    const bottles = bottleSource.filter(resource => resource?.type === 'product');
    bottleSource.filter(resource => resource?.type === 'taste_box').forEach(box => {
      (Array.isArray(box.components) ? box.components : []).forEach(component => bottles.push({
        ...component,
        type: 'product',
        source_type: 'taste_box',
        taste_box_id: text(box.id),
        taste_box_label: text(box.label),
        taste_box_url: text(box.url),
        quantity: Math.min(12, Math.max(1, Math.round(Number(component?.quantity) || 1))),
      }));
    });
    const seen = new Set();
    return bottles.filter(bottle => {
      const id = text(bottle?.id);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  function bottleMode(content, resources) {
    const mode = text(content?.visual_mode) || 'auto';
    if (['bottles', 'bottles-grid', 'bottles-diagonal', 'bottles-tilted', 'bottles-fan'].includes(mode)) return mode;
    return mode === 'auto' && !text(content?.image_url || content?.mobile_image_url)
      && bottleResources(resources).some(wine => text(wine.neutral_image_url)) ? 'bottles-auto' : '';
  }

  function vinpostenHeroImages(resources) {
    const source = Array.isArray(resources) ? resources : [];
    const first = source.find(resource => resource?.type === 'product' || resource?.type === 'taste_box');
    if (first?.type === 'taste_box') {
      const components = bottleResources([first]).flatMap(resource =>
        Array.from({ length: Math.min(6, Number(resource.quantity) || 1) }, () => resource.neutral_image_url || resource.image_url));
      const images = components.filter(Boolean).slice(0, 6);
      return images.length ? images : [first.image_url].filter(Boolean);
    }
    return source.filter(resource => resource?.type === 'product')
      .map(resource => resource.neutral_image_url || resource.image_url).filter(Boolean).slice(0, 3);
  }

  // Each device uses the same renderer with its own expression. CSS switches the
  // variants at the storefront breakpoint, including after a viewport resize.
  function renderResponsiveArtwork(item, render) {
    const desktop = text(item.visual_mode) || 'auto';
    const mobile = text(item.mobile_visual_mode) || (item.mobile_content_mode === 'title-button' ? 'title-button' : desktop);
    if (desktop === mobile) return render({ ...item, visual_mode: desktop });
    const wrapper = element('div', css + 'device-artwork');
    for (const [device, mode] of [['desktop', desktop], ['mobile', mobile]]) {
      const artwork = render({ ...item, visual_mode: mode });
      if (artwork) {
        artwork.classList.add(css + 'artwork--' + device);
        wrapper.appendChild(artwork);
      }
    }
    return wrapper.childElementCount ? wrapper : null;
  }

  const sixBottleDepth = [
    [-25, -15, .69, -10], [0, -22, .69, 0], [25, -15, .69, 10],
    [-14, -4, .83, -6], [14, -4, .83, 6], [0, 11, 1, 0],
  ];

  function renderWineLineup(resources, mode, primary = false, imageProductIds = null, tasteBoxId = '', imageCaption = '', automaticCaptionEnabled = true, bottleRepeat = '') {
    const available = bottleResources(resources, tasteBoxId)
      .filter(resource => text(resource.neutral_image_url) && text(resource.url));
    const hasTasteBoxComponents = available.some(resource => resource.source_type === 'taste_box');
    const selected = Array.isArray(imageProductIds)
      ? [...new Set(imageProductIds.map(String))].map(id => available.find(wine => String(wine.id) === id)).filter(Boolean)
      : available.slice(0, hasTasteBoxComponents ? 6 : 3);
    const expanded = selected.flatMap(resource => Array.from(
      { length: resource.source_type === 'taste_box' ? Math.min(12, Math.max(1, Number(resource.quantity) || 1)) : 1 },
      () => resource,
    ));
    let wines = selected.some(resource => resource.source_type === 'taste_box')
      ? expanded.slice(0, 12)
      : expanded;
    // Repetition changes the artwork only; taste-box quantities and product
    // membership remain authoritative when more than one bottle is selected.
    const repeated = selected.length === 1 && selected[0].source_type !== 'taste_box'
      && ['three', 'five', 'depth-three', 'depth-five', 'depth-six'].includes(bottleRepeat);
    const depth = repeated && bottleRepeat.startsWith('depth-');
    if (repeated) wines = Array.from({ length: bottleRepeat === 'depth-six' ? 6 : bottleRepeat.endsWith('three') ? 3 : 5 }, () => selected[0]);
    if (!wines.length) return null;
    const count = wines.length;
    let columns = count;
    if (mode === 'bottles-auto') {
      columns = count <= 3 ? count : count === 4 ? 2 : count <= 6 ? 3 : count <= 8 ? 4 : Math.ceil(Math.sqrt(count));
    } else if (mode === 'bottles-grid') {
      columns = Math.min(3, count);
    } else if (mode === 'bottles-diagonal') {
      columns = count;
    }
    if (depth) columns = 1;
    const rows = depth || mode === 'bottles-diagonal' ? 1 : Math.ceil(count / columns);
    const groupKeys = new Set();
    const automaticCaption = selected.flatMap(resource => {
      const tasteBox = resource.source_type === 'taste_box' && text(resource.taste_box_label);
      const key = tasteBox ? 'taste-box:' + text(resource.taste_box_id || resource.taste_box_label) : 'wine:' + text(resource.id);
      if (groupKeys.has(key)) return [];
      groupKeys.add(key);
      return [tasteBox ? resource.taste_box_label : resource.label];
    }).map(text).filter(Boolean).join(' · ');
    const captionText = text(imageCaption) || (automaticCaptionEnabled ? automaticCaption : '');
    const selectedBox = available.find(resource => resource.source_type === 'taste_box' && text(resource.taste_box_id) === imageTasteBoxId(tasteBoxId));
    const captionTarget = selectedBox && text(selectedBox.taste_box_url);
    const list = element('ul', css + 'wine-lineup ' + css + 'wine-lineup--' + mode + (depth ? ' ' + css + 'wine-lineup--depth' : '') + (captionText ? ' ' + css + 'wine-lineup--captioned' : ''));
    list.setAttribute('aria-label', captionText ? captionText + ': Flasker i kampagnen' : 'Flasker i kampagnen');
    list.style.setProperty('--bottle-count', String(count));
    list.style.setProperty('--bottle-columns', String(columns));
    list.style.setProperty('--bottle-rows', String(rows));
    list.style.setProperty('--bottle-row-scale', depth ? '.8' : '1');
    let fitImages = () => {};
    const pyramid = count === 6 && depth ? sixBottleDepth : null;
    wines.forEach((resource, index) => {
      const item = element('li', css + 'wine-lineup-item');
      const depthOffset = depth ? (count === 3 ? [-1, 1, 0] : count === 5 ? [-2, 2, -1, 1, 0] : [])[index] : 0;
      const angle = pyramid ? pyramid[index][3] : depth ? depthOffset * 5 : mode === 'bottles-tilted' ? 13 : mode === 'bottles-fan' && wines.length > 1
        ? (index / (wines.length - 1) - .5) * 28 : 0;
      item.style.setProperty('--bottle-angle', angle + 'deg');
      if (depth) {
        item.style.setProperty('--bottle-column', '1');
        item.style.setProperty('--bottle-row', '1');
        item.style.setProperty('--bottle-depth-x', (pyramid ? pyramid[index][0] : depthOffset * 12) + '%');
        item.style.setProperty('--bottle-depth-y', pyramid ? pyramid[index][1] + '%' : depthOffset ? '-5%' : '6%');
        item.style.setProperty('--bottle-depth-scale', String(pyramid ? pyramid[index][2] : 1 - Math.abs(depthOffset) * .09));
        item.style.zIndex = String(index + 1);
      } else if (mode === 'bottles-diagonal') {
        item.dataset.bottleLevel = String(Math.floor(index * 3 / count) - 1);
      }
      const link = element('a', css + 'wine-lineup-link');
      link.href = resource.url;
      link.setAttribute('aria-label', text(resource.label));
      const image = element('img');
      image.src = resource.neutral_image_url;
      image.alt = text(resource.label);
      if (repeated && index !== (depth ? count - 1 : 0)) {
        item.setAttribute('aria-hidden', 'true');
        link.tabIndex = -1;
        image.alt = '';
      }
      image.loading = primary ? 'eager' : 'lazy';
      image.decoding = 'async';
      link.appendChild(image);
      item.appendChild(link);
      list.appendChild(item);
      void image.decode().then(() => fitImages(), () => fitImages());
      void measureVisibleBottle(resource.neutral_image_url).then(bounds => {
        if (!item.isConnected) return;
        const visible = bounds || { centerX: .5, centerY: .5, width: .2, height: 1 };
        item.dataset.bottleVisibleCenterX = String(visible.centerX);
        item.dataset.bottleVisibleCenterY = String(visible.centerY);
        item.dataset.bottleVisibleWidth = String(visible.width);
        item.dataset.bottleVisibleHeight = String(visible.height);
        item.dataset.bottleVisibleScale = '1';
        fitImages();
      });
    });
    if (captionText) {
      const caption = element('li', css + 'wine-lineup-caption');
      if (captionTarget) {
        const link = element('a');
        link.href = captionTarget;
        link.textContent = captionText;
        link.setAttribute('aria-label', 'Se smagekassen ' + captionText);
        caption.appendChild(link);
      } else caption.textContent = captionText;
      list.appendChild(caption);
    }
    if (typeof ResizeObserver === 'function') {
      let frame = 0;
      fitImages = () => {
        frame = 0;
        const cells = [...list.querySelectorAll('.' + css + 'wine-lineup-item')];
        if (!cells.length || !list.isConnected || cells.some(cell => !cell.dataset.bottleVisibleHeight)) return;
        const cellWidths = cells.map(cell => cell.clientWidth).filter(Boolean);
        if (!cellWidths.length) return;
        const cellWidth = Math.min(...cellWidths);
        // Share a canvas scale so the shorter silhouettes keep their height.
        const sharedScale = Math.min(3.2, 1 / Math.max(.25, ...cells.map(cell => Number(cell.dataset.bottleVisibleHeight))));
        // Alpha measurement removes transparent source padding. The measured
        // visible ratio also keeps neighbouring bottles separate after rotation.
        const widthFactor = Math.max(...cells.map(cell => {
          const angle = Math.abs(parseFloat(cell.style.getPropertyValue('--bottle-angle')) || 0) * Math.PI / 180;
          const image = cell.querySelector('img');
          const visibleWidth = Number(cell.dataset.bottleVisibleWidth) || .18;
          const visibleHeight = Number(cell.dataset.bottleVisibleHeight) || 1;
          const imageRatio = image?.naturalWidth && image?.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
          const visibleRatio = imageRatio * visibleWidth / visibleHeight;
          const rotated = depth || ['bottles-tilted', 'bottles-fan'].includes(mode);
          const factor = visibleRatio * Math.cos(angle) + Math.sin(angle) * (rotated ? .15 : 1);
          return depth ? factor * (Number(cell.style.getPropertyValue('--bottle-depth-scale')) || 1)
            / (1 - 2 * Math.abs(parseFloat(cell.style.getPropertyValue('--bottle-depth-x')) || 0) / 100) : factor;
        }));
        if (cellWidth && widthFactor) list.style.setProperty('--bottle-image-height', Math.floor(cellWidth / widthFactor) + 'px');
        const caption = list.querySelector('.' + css + 'wine-lineup-caption');
        const diagonal = !depth && mode === 'bottles-diagonal' && count >= 3;
        let diagonalTop = 0, diagonalTravel = 0;
        if (diagonal) {
          const lineupBox = list.getBoundingClientRect();
          const sectionBox = list.closest('.' + css + 'hero,.' + css + 'copy,.' + css + 'focus-panel')?.getBoundingClientRect();
          diagonalTop = Math.max(lineupBox.top + 4, sectionBox ? sectionBox.top + Math.max(12, sectionBox.height * .025) : lineupBox.top + 4);
          const bottom = Math.min(lineupBox.bottom - 4, sectionBox ? sectionBox.bottom - 12 : lineupBox.bottom - 4,
            caption ? caption.getBoundingClientRect().top - 10 : Infinity);
          const tallestBottle = Math.max(...cells.map(cell => {
            const image = cell.querySelector('img');
            return image.offsetHeight * sharedScale * (Number(cell.dataset.bottleVisibleHeight) || 1);
          }));
          diagonalTravel = Math.max(0, bottom - diagonalTop - tallestBottle);
        }
        cells.forEach(cell => {
          const image = cell.querySelector('img');
          if (!image) return;
          const scale = sharedScale;
          cell.dataset.bottleVisibleScale = String(scale);
          const centerX = Number(cell.dataset.bottleVisibleCenterX) || .5;
          const centerY = Number(cell.dataset.bottleVisibleCenterY) || .5;
          const visibleRatio = Number(cell.dataset.bottleVisibleHeight) || 1;
          const offsetX = (.5 - centerX) * image.offsetWidth * scale;
          const level = Number(cell.dataset.bottleLevel) || 0;
          const visibleHeight = image.offsetHeight * scale * visibleRatio;
          const nearCaption = captionText && !diagonal ? Math.max(0, (cell.clientHeight - visibleHeight) / 2) - 10 : 0;
          let offsetY = (.5 - centerY) * image.offsetHeight * scale + nearCaption;
          const positionImage = () => {
            image.style.transform = `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(${scale})`;
          };
          positionImage();
          if (diagonal) {
            const targetTop = diagonalTop + diagonalTravel * (level + 1) / 2;
            const imageBox = image.getBoundingClientRect();
            const visibleTop = imageBox.top + imageBox.height * (centerY - visibleRatio / 2);
            offsetY += targetTop - visibleTop;
            positionImage();
          }
          if (caption) {
            const imageBox = image.getBoundingClientRect();
            const visibleBottom = imageBox.top + imageBox.height * (centerY + visibleRatio / 2);
            const overlap = visibleBottom - caption.getBoundingClientRect().top + 10;
            if (overlap > 0) {
              offsetY -= overlap;
              positionImage();
            }
          }
        });
      };
      const observer = new ResizeObserver(() => {
        if (!frame) frame = requestAnimationFrame(fitImages);
      });
      observer.observe(list);
      list.campaignLineupCleanup = () => { observer.disconnect(); cancelAnimationFrame(frame); };
      frame = requestAnimationFrame(fitImages);
    }
    return list;
  }

  function bannerLayout(value) {
    if (typeof value === 'string') { try { value = JSON.parse(value); } catch { value = null; } }
    const layout = Object.fromEntries(['text', 'button', 'wines'].map(key => [key,
      /^(top|middle|bottom)-(left|center|right)$/.test(value?.[key])
        || key === 'button' && /^below-(left|center|right)$/.test(value?.[key]) ? value[key] : 'auto',
    ]));
    if (value?.copy_style === 'custom') layout.copy_style = 'custom';
    if (['none', 'roomy', 'wide'].includes(value?.button_spacing)) layout.button_spacing = value.button_spacing;
    if (value?.image === 'right' || value?.image === 'cover') layout.image = value.image;
    if (typeof value?.palette === 'string') layout.palette = value.palette;
    if (['solid', 'gradient'].includes(value?.background_style)) layout.background_style = value.background_style;
    if (layout.image === 'right' && value?.image_width != null && Number.isFinite(Number(value.image_width))) {
      const width = Math.max(35, Math.min(60, Math.round(Number(value.image_width))));
      if (width !== 50) layout.image_width = width;
    }
    return layout;
  }

  function bannerAnchor(position, width, height, area) {
    const [vertical, horizontal] = position.split('-');
    return { x: area.x + (area.width - width) * ({ left: 0, center: .5, right: 1 }[horizontal]),
      y: area.y + (area.height - height) * ({ top: 0, middle: .5, bottom: 1 }[vertical]), width, height };
  }

  // Keep all maximal free rectangles, including overlapping alternatives. This
  // leaves room beside OR below copy, without treating the image as an overlay.
  function bannerFreeAreas(areas, obstacle, gap) {
    const left = obstacle.x - gap, right = obstacle.x + obstacle.width + gap;
    const top = obstacle.y - gap, bottom = obstacle.y + obstacle.height + gap;
    return areas.flatMap(area => {
      const endX = area.x + area.width, endY = area.y + area.height;
      if (right <= area.x || left >= endX || bottom <= area.y || top >= endY) return [area];
      return [
        { ...area, width: left - area.x }, { ...area, x: right, width: endX - right },
        { ...area, height: top - area.y }, { ...area, y: bottom, height: endY - bottom },
      ].filter(box => box.width > 0 && box.height > 0);
    });
  }

  function applyBannerLayout(section, item) {
    const layouts = { desktop: bannerLayout(item.banner_layout), mobile: bannerLayout(item.mobile_banner_layout) };
    const photoHero = section.classList.contains(css + 'hero--photo');
    const vinposten = section.classList.contains(css + 'hero--vinposten');
    const sharedPhotoCopy = photoHero && layouts.desktop.copy_style !== 'custom';
    section.classList.toggle(css + 'hero--shared-copy', sharedPhotoCopy);
    if (!Object.values(layouts).some(layout => Object.entries(layout).some(([key, value]) => value !== 'auto' && !(key === 'image' && value === 'cover')))) return;
    section.classList.add(css + 'banner-layout');
    const content = section.querySelector('.' + css + 'hero-content,.' + css + 'copy-content');
    if (content) {
      const copy = element('div', css + 'banner-text');
      [...content.children].filter(node => !node.classList.contains(css + 'button')).forEach(node => copy.appendChild(node));
      content.prepend(copy);
    }
    const copy = content?.querySelector('.' + css + 'banner-text');
    const button = content?.querySelector('.' + css + 'button');
    const wines = section.querySelector('.' + css + 'wine-lineup');
    const media = window.matchMedia('(max-width: 640px)');
    const set = (name, value) => section.style.setProperty('--banner-' + name, value);
    const place = (name, box) => {
      set(name + '-x', box.x + 'px'); set(name + '-y', box.y + 'px');
    };
    const layoutBanner = () => {
      const mobile = media.matches;
      const sharedPhotoLayout = mobile && photoHero;
      const layout = layouts[mobile ? 'mobile' : 'desktop'];
      const palette = item.color_palettes?.[layout.palette || layouts.desktop.palette];
      section.dataset.holidayPalette = palette?.id || '';
      section.dataset.bannerBackground = layout.background_style || layouts.desktop.background_style || '';
      const existingOrnaments=section.querySelector('[data-campaign-ornaments]');
      if(existingOrnaments?.dataset.theme!==palette?.id){
        existingOrnaments?.remove();
        if(palette?.ornaments){
          const layer=element('span');layer.dataset.campaignOrnaments='';layer.dataset.theme=palette.id;layer.setAttribute('aria-hidden','true');
          layer.style.cssText='position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:0';
          for(const [edge,url]of Object.entries(palette.ornaments)){
            if(!['top','bottom','left','right'].includes(edge)||!/^\/campaign-themes\/[a-z0-9-]+\.png$/.test(url))continue;
            const image=element('img');image.alt='';image.src='https://app.mundskaenken.com'+url;
            image.style.cssText=`position:absolute;${edge}:0;${['top','bottom'].includes(edge)?'left:0;width:100%;height:auto':'top:0;width:6%;height:100%'};pointer-events:none`;
            layer.appendChild(image);
          }
          section.appendChild(layer);
        }
      }
      if (palette) Object.entries(palette).forEach(([key, value]) => {
        if (/^#[0-9a-f]{6}$/i.test(value)) section.style.setProperty('--holiday-' + key, value);
      });
      const spacing = layout.button_spacing || item.button_spacing || 'normal';
      section.style.setProperty('--hero-button-gap', (spacing === 'none' ? 0 : spacing === 'wide' ? (mobile ? 50 : 68) : spacing === 'roomy' ? (mobile ? 32 : 42) : 18) + 'px');
      const inheritedImage = mobile && !layout.image && layouts.desktop.image === 'right';
      const imageRight = !vinposten && !sharedPhotoLayout && Boolean(section.querySelector('picture')) &&
        (layout.image === 'right' || inheritedImage);
      const imageWidth = (inheritedImage ? layouts.desktop.image_width : layout.image_width) || 50;
      set('image-width', imageWidth + '%');
      const active = !vinposten && !sharedPhotoLayout && !sharedPhotoCopy && (imageRight || Object.entries(layout).some(([key, value]) => !['palette', 'background_style'].includes(key) && value !== 'auto'));
      section.classList.toggle(css + 'banner-layout--active', active);
      section.classList.toggle(css + 'banner--image-right', imageRight);
      if (!active || !copy || !section.clientWidth || !section.clientHeight) return;
      const grouped = !button || layout.button === 'auto' || layout.button.startsWith('below-');
      section.classList.toggle(css + 'banner-layout--grouped', grouped);
      const width = section.clientWidth, height = section.clientHeight, gap = 18;
      const insetX = wines && !mobile ? .03 : .07;
      const insetY = wines && !mobile ? .04 : .08;
      const area = { x: width * insetX, y: height * insetY,
        width: width * (1 - 2 * insetX), height: height * (1 - 2 * insetY) };
      if (imageRight) {
        area.x = width * (mobile ? .06 : .05);
        area.y = height * (mobile ? .055 : .08);
        area.width = width * (mobile ? .88 : (1 - imageWidth / 100 - .10));
        area.height = height * (mobile ? .36 : .84);
      }
      if (section.classList.contains(css + 'hero--vinposten')) {
        area.x = width * (mobile ? .06 : .04);
        area.y = height * (mobile ? .33 : .06);
        area.width = width * (mobile ? .88 : window.innerWidth <= 1199 ? .525 : .414);
        area.height = height * (mobile ? .62 : .88);
      }
      const credit = section.querySelector('.' + css + 'photo-credit');
      if (credit) area.height = Math.min(area.height, height - area.y - credit.getBoundingClientRect().height - 10 - gap);
      const textPosition = layout.text === 'auto' || imageRight && mobile
        ? (imageRight ? (mobile ? 'top-left' : 'middle-left') : item.visual_mode === 'title-button' ? 'middle-center'
          : wines ? (mobile ? 'top-center' : 'middle-left') : (mobile ? 'bottom-center' : 'bottom-left')) : layout.text;
      const winePosition = layout.wines === 'auto' ? (mobile ? 'bottom-center' : 'middle-right') : layout.wines;
      set('text-align', textPosition.split('-')[1]);
      const buttonAlign = layout.button.startsWith('below-') ? layout.button.slice(6) : textPosition.split('-')[1];
      set('button-margin-left', buttonAlign === 'left' ? '0px' : 'auto');
      set('button-margin-right', buttonAlign === 'right' ? '0px' : 'auto');
      let copyWidth = area.width;
      if (wines && !mobile) {
        const depthHero = section.classList.contains(css + 'hero') && wines.classList.contains(css + 'wine-lineup--depth');
        copyWidth = width * (depthHero ? .46 : .35);
        const heading = copy.querySelector('h2,h3');
        const words = heading?.textContent.trim().split(/\s+/).filter(Boolean) || [];
        if (words.length) {
          const probe = element('span');
          probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap';
          heading.appendChild(probe);
          const wordWidth = Math.max(...words.map(word => {
            probe.textContent = word;
            return probe.getBoundingClientRect().width;
          }));
          probe.remove();
          copyWidth = Math.min(width * .46, Math.max(copyWidth, wordWidth + 10));
        }
      }
      set('copy-width', copyWidth + 'px');
      set('copy-scale', 1);
      set('button-max-width', area.width + 'px');
      const textNode = grouped ? content : copy;
      const natural = textNode.getBoundingClientRect();
      const buttonBox = !grouped ? button.getBoundingClientRect() : null;
      // Reserve useful space even for long headings. Scale the complete copy
      // group only when it cannot fit; the banner itself never grows with wine count.
      const maxHeight = Math.min(area.height - (buttonBox ? buttonBox.height + gap : 0) - (wines ? Math.min(150, area.height * .28) : 0),
        area.height - (buttonBox ? 2 * (buttonBox.height + gap) : 0));
      const scale = Math.min(1, Math.max(1, maxHeight) / Math.max(1, natural.height));
      set('copy-scale', scale);
      const copyBox = bannerAnchor(textPosition, natural.width * scale, natural.height * scale, area);
      place('text', copyBox);
      let free = bannerFreeAreas([area], copyBox, gap);
      const distance = (box, target) => Math.hypot((box.x - target.x) / area.width, (box.y - target.y) / area.height);
      if (buttonBox) {
        const target = bannerAnchor(layout.button, buttonBox.width, buttonBox.height, area);
        target.y += spacing === 'wide' ? (mobile ? 38 : 50) : spacing === 'roomy' ? (mobile ? 22 : 32) : spacing === 'none' ? (mobile ? -8 : -12) : 0;
        const candidates = free.filter(box => box.width >= buttonBox.width && box.height >= buttonBox.height)
          .map(box => ({ ...target, x: Math.max(box.x, Math.min(target.x, box.x + box.width - target.width)),
            y: Math.max(box.y, Math.min(target.y, box.y + box.height - target.height)) }));
        candidates.sort((a, b) => distance(a, target) - distance(b, target));
        const chosen = candidates[0];
        if (chosen) { place('button', chosen); free = bannerFreeAreas(free, chosen, gap); }
      }
      if (wines) {
        const targetWidth = width * (mobile ? .9 : .62), targetHeight = height * (mobile ? .54 : .92);
        const target = bannerAnchor(winePosition, targetWidth, targetHeight, area);
        const wineItems = [...wines.querySelectorAll('.' + css + 'wine-lineup-item')];
        const count = wineItems.length;
        const columns = Number(wines.style.getPropertyValue('--bottle-columns')) || count;
        const rows = Number(wines.style.getPropertyValue('--bottle-rows')) || 1;
        const candidates = free.map(box => {
          const placed = bannerAnchor(winePosition, Math.min(box.width, targetWidth), Math.min(box.height, targetHeight), box);
          const bottleSize = Math.min(placed.height / rows, placed.width / Math.max(1, columns * .5));
          return { ...placed, score: bottleSize / (1 + distance(placed, target) * .4) };
        }).sort((a, b) => b.score - a.score);
        const chosen = candidates[0];
        if (chosen) {
          place('wines', chosen); set('wines-width', chosen.width + 'px'); set('wines-height', chosen.height + 'px');
          // Padding contains the rotated bottle cells as well as the straight ones.
          const sine = Math.max(0, ...wineItems.map(node => Math.abs(Math.sin((parseFloat(node.style.getPropertyValue('--bottle-angle')) || 0) * Math.PI / 180))));
          set('wines-padding-x', Math.min(chosen.width * .16, 6 + chosen.height * sine * .42) + 'px');
          set('wines-padding-y', Math.min(chosen.height * .1, 4 + chosen.width / columns * sine * .22) + 'px');
        }
      }
    };
    let frame = 0, disposed = false;
    const schedule = () => { if (!disposed && !frame) frame = requestAnimationFrame(() => { frame = 0; layoutBanner(); }); };
    const observer = new ResizeObserver(schedule);
    observer.observe(section);
    media.addEventListener('change', schedule);
    document.fonts?.ready.then(schedule);
    section.campaignBannerRelayout = layoutBanner;
    section.campaignBannerCleanup = () => {
      disposed = true; cancelAnimationFrame(frame); observer.disconnect(); media.removeEventListener('change', schedule);
      section.campaignBannerRelayout = null;
    };
    layoutBanner();
  }

  function renderHeroSlide(hero, primary, resources, sharedLayout) {
    return renderResponsiveArtwork(hero, item => renderHeroArtwork(item, primary, resources, sharedLayout));
  }

  function renderTasteBoxGallery(resources, primary) {
    const boxes = resources.filter(box => box.type === 'taste_box' && box.available_for_sale !== false
      && text(box.label) && /^\/products\/[^/?#]+$/.test(text(box.url))).slice(0, 4);
    if (!boxes.length) return null;
    const gallery = element('ul', css + 'taste-box-gallery');
    for (const box of boxes) {
      const tile = element('li', css + 'taste-box-tile');
      const link = element('a', css + 'taste-box-tile-link');
      link.href = box.url;
      const art = element('span', css + 'taste-box-tile-art');
      art.setAttribute('aria-hidden', 'true');
      const components = bottleResources([box]);
      const bottles = components.flatMap(wine => Array.from({ length: wine.quantity }, () => wine));
      if (bottles.length && bottles.length <= 6 && bottles.every(wine => text(wine.neutral_image_url))) {
        const positions = sixBottleDepth.slice(-bottles.length);
        if (bottles.length === 4) positions[0] = sixBottleDepth[1];
        if (bottles.length === 2) positions[0] = [-14, -4, .83, -6];
        bottles.forEach((wine, index) => {
          const [x, y, scale, angle] = positions[index];
          const image = element('img');
          image.src = wine.neutral_image_url;
          image.alt = '';
          image.loading = primary ? 'eager' : 'lazy';
          image.style.setProperty('--box-bottle-x', x * 1.3 + '%');
          image.style.setProperty('--box-bottle-y', y * .8 + '%');
          image.style.setProperty('--box-bottle-scale', String(scale));
          image.style.setProperty('--box-bottle-angle', angle + 'deg');
          art.appendChild(image);
        });
      }
      link.appendChild(art);
      addText(link, 'span', box.label, css + 'taste-box-tile-name');
      tile.appendChild(link);
      gallery.appendChild(tile);
    }
    return gallery;
  }

  function renderHeroArtwork(hero, primary, resources, sharedLayout) {
    const tasteBoxGallery = hero.visual_mode === 'taste-box-gallery';
    const gallery = tasteBoxGallery ? renderTasteBoxGallery(resources, primary) : null;
    if (tasteBoxGallery && !gallery) return null;
    const legacyVinposten = /(?:^|\/)campaign-vinposten-hero\.svg(?:\?|$)/.test(text(hero.image_url));
    const vinposten = (!tasteBoxGallery && legacyVinposten) || hero.visual_mode === 'vinposten-photo';
    const vinpostenPhoto = vinposten && ((!legacyVinposten && text(hero.image_url)) || text(hero.mobile_image_url));
    const mode = vinposten || tasteBoxGallery ? '' : bottleMode(hero, resources);
    const hasButton = Boolean(text(hero.cta_label) && text(hero.link_target));
    if (!gallery && ![hero.image_url, hero.mobile_image_url, hero.eyebrow, hero.headline, hero.subline].some(text) && !hasButton) return null;
    const lineup = mode ? renderWineLineup(resources, mode, primary, hero.image_product_ids, hero.image_taste_box_id, '', false, hero.image_bottle_repeat) : null;
    const section = element("article", css + "hero");
    if (sharedLayout) section.classList.add(css + "hero--locked-copy");
    if (['none', 'roomy', 'wide'].includes(hero.button_spacing)) section.dataset.buttonSpacing = hero.button_spacing;
    if (hero.visual_mode === 'title-button') {
      section.classList.add(css + 'title-button');
      if (!text(hero.image_url || hero.mobile_image_url)) section.classList.add(css + 'title-button--plain');
    }
    setResponsiveImageCrop(section, hero);
    if (vinposten) {
      section.classList.add(css + 'hero--vinposten');
      if (vinpostenPhoto) {
        const picture = element('picture', css + 'vinposten-photo');
        const image = element('img');
        image.alt = '';
        if (text(hero.mobile_image_url)) addMobileSource(picture, hero.mobile_image_url);
        prepareHeroImage(image, vinpostenPhoto, primary);
        picture.appendChild(image);
        section.appendChild(picture);
      }
      const logo = element('img', css + 'vinposten-logo');
      logo.src = legacyVinposten
        ? new URL('campaign-vinposten-logo.svg', new URL(hero.image_url, window.location.href)).toString()
        : 'https://app.mundskaenken.com/campaign-vinposten-logo.svg';
      logo.alt = '';
      section.appendChild(logo);
      if (vinpostenPhoto) appendPhotoCredit(section, hero);
    }
    if (!tasteBoxGallery && !vinposten && text(hero.image_url || hero.mobile_image_url)) {
      if (lineup) section.classList.add(css + 'bottle-feature--backdrop');
      const image = element('img');
      image.alt = '';
      prepareHeroImage(image, hero.image_url || hero.mobile_image_url, primary);
      const picture = element('picture');
      if (text(hero.mobile_image_url)) addMobileSource(picture, hero.mobile_image_url);
      picture.appendChild(image);
      section.appendChild(picture);
      if (!lineup) section.classList.add(css + 'hero--photo');
      appendPhotoCredit(section, hero);
    }
    const content = element("div", css + "hero-content");
    addText(content, "p", hero.eyebrow, css + "kicker");
    const headline = addText(content, "h2", hero.headline);
    if (headline?.textContent.includes("\n")) headline.classList.add(css + "headline--multiline");
    addText(content, "p", hero.subline, css + "intro");
    appendLink(content, hero.cta_label, hero.link_target, css + "button button button--secondary");
    if (content.childElementCount) section.appendChild(content);
    if (vinposten) {
      const images = vinpostenHeroImages(resources);
      if (images.length) {
        const backdrop = element('div', css + 'vinposten-products');
        backdrop.setAttribute('aria-hidden', 'true');
        backdrop.style.setProperty('--vinposten-image-count', String(images.length));
        images.forEach(source => {
          const image = element('img');
          image.src = source;
          image.alt = '';
          image.loading = primary ? 'eager' : 'lazy';
          backdrop.appendChild(image);
        });
        section.appendChild(backdrop);
      }
    }
    if (lineup) {
      section.classList.add(css + 'bottle-feature');
      section.appendChild(lineup);
    }
    if (gallery) {
      section.classList.add(css + 'hero--taste-box-gallery');
      section.appendChild(gallery);
      applyBannerLayout(section, {
        ...hero,
        banner_layout: { palette: hero.banner_layout?.palette, background_style: hero.banner_layout?.background_style },
        mobile_banner_layout: { palette: hero.mobile_banner_layout?.palette, background_style: hero.mobile_banner_layout?.background_style },
      });
    } else {
      applyBannerLayout(section, hero);
    }
    return section;
  }

  function createRotator(panels, options) {
    if (!panels.length) return null;
    if (panels.length === 1) return panels[0];
    const wrapper = element("section", options.className);
    const track = element("div", options.trackClassName);
    track.append(...panels);
    wrapper.appendChild(track);

    let current = 0;
    let timer = 0;
    const reducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const autoplayDisabled = options.autoplay === false || reducedMotion;
    let manuallySelected = false;
    let swipePointerId = null;
    let swipeStartX = 0;
    let swipeStartY = 0;
    let suppressSwipeClickUntil = 0;
    let dots = [];

    function showPanel(index) {
      current = (index + panels.length) % panels.length;
      panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== current;
      });
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === current) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    }

    function startRotation() {
      window.clearInterval(timer);
      timer = 0;
      if (autoplayDisabled || manuallySelected || swipePointerId !== null) return;
      if (options.continuousAutoplay && wrapper.contains(document.activeElement)
        && document.activeElement.matches(":focus-visible")) return;
      const interval = clamp(options.intervalSeconds, options.fallbackSeconds, options.minimumSeconds, options.maximumSeconds) * 1000;
      timer = window.setInterval(() => showPanel(current + 1), interval);
    }

    const controls = element("div", options.controlsClassName);
    controls.setAttribute("aria-label", "Vælg " + options.itemLabel);
    dots = panels.map((panel, panelIndex) => {
      const dot = element("button", css + "rotation-dot");
      dot.type = "button";
      dot.setAttribute("aria-label", `Vis ${options.itemLabel} ${panelIndex + 1} af ${panels.length}`);
      dot.addEventListener("click", () => {
        manuallySelected = !options.continuousAutoplay;
        showPanel(panelIndex);
        startRotation();
      });
      controls.appendChild(dot);
      return dot;
    });
    wrapper.appendChild(controls);
    if (options.continuousAutoplay) {
      wrapper.addEventListener("focusin", startRotation);
      wrapper.addEventListener("focusout", () => queueMicrotask(startRotation));
    } else {
      wrapper.addEventListener("mouseenter", () => window.clearInterval(timer));
      wrapper.addEventListener("mouseleave", startRotation);
      wrapper.addEventListener("focusin", () => window.clearInterval(timer));
      wrapper.addEventListener("focusout", startRotation);
    }
    wrapper.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" || event.isPrimary === false) return;
      swipePointerId = event.pointerId;
      swipeStartX = event.clientX;
      swipeStartY = event.clientY;
      window.clearInterval(timer);
    });
    wrapper.addEventListener("pointerup", (event) => {
      if (event.pointerId !== swipePointerId) return;
      const deltaX = event.clientX - swipeStartX;
      const deltaY = event.clientY - swipeStartY;
      swipePointerId = null;
      if (Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        manuallySelected = !options.continuousAutoplay;
        suppressSwipeClickUntil = Date.now() + 500;
        showPanel(current + (deltaX < 0 ? 1 : -1));
      }
      startRotation();
    });
    wrapper.addEventListener("pointercancel", () => {
      swipePointerId = null;
      startRotation();
    });
    wrapper.addEventListener("click", (event) => {
      if (Date.now() >= suppressSwipeClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
      suppressSwipeClickUntil = 0;
    }, true);
    showPanel(0);
    startRotation();
    return wrapper;
  }

  function renderHero(campaign) {
    const slides = (Array.isArray(campaign.hero_slides) && campaign.hero_slides.length
      ? campaign.hero_slides
      : [campaign.hero || {}])
      .map((hero, index) => {
        const resources = Array.isArray(campaign.resources) ? campaign.resources : [];
        const selected = Array.isArray(hero.storefront_resources) ? hero.storefront_resources : hero.resources?.length
          ? hero.resources.map(wine => resources.find(live => live.id === wine.id && live.type === wine.type)).filter(Boolean)
          : resources;
        return renderHeroSlide(hero, index === 0, selected, campaign.display?.hero_shared_layout === true);
      })
      .filter(Boolean);
    if (!slides.length) return null;
    const display = campaign.display || {};
    if (!display.hero_rotation_enabled || slides.length < 2) return slides[0];
    return createRotator(slides, {
      className: css + "hero-rotation",
      trackClassName: css + "hero-track",
      controlsClassName: css + "hero-controls",
      itemLabel: "banner",
      autoplay: true,
      continuousAutoplay: true,
      intervalSeconds: display.hero_rotation_seconds,
      fallbackSeconds: 8,
      minimumSeconds: 3,
      maximumSeconds: 60,
    });
  }

  function initProductCarousel(track) {
    if (!track || track.children.length < 2) return;
    track.classList.add(css + "products--carousel");
    track.id = 'campaign-products-' + (++productCarouselId);
    track.tabIndex = 0;
    track.setAttribute("aria-label", "Udvalgte produkter. Brug piletasterne til at bladre.");
    const controls = element('div', css + 'product-controls');
    controls.hidden = true;
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', 'Bladr i produkterne');
    const previous = element('button'), next = element('button');
    for (const [button, label, arrow] of [[previous, 'Forrige produkter', '←'], [next, 'Næste produkter', '→']]) {
      button.type = 'button';
      button.textContent = arrow;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-controls', track.id);
    }
    const status = element('span');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    controls.append(previous, status, next);
    track.parentElement.appendChild(controls);

    const reducedMotion = typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = 0;
    const maximum = () => Math.max(0, track.scrollWidth - track.clientWidth);
    function refresh() {
      frame = 0;
      const max = maximum();
      controls.hidden = !track.clientWidth || max <= 2;
      previous.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft >= max - 2;
      const bounds = track.getBoundingClientRect();
      const visible = Array.from(track.children).map((item, index) => ({ index, rect: item.getBoundingClientRect() }))
        .filter(({ rect }) => rect.right > bounds.left + 2 && rect.left < bounds.right - 2);
      if (visible.length) {
        const label = `${visible[0].index + 1}–${visible.at(-1).index + 1} af ${track.children.length}`;
        if (status.textContent !== label) status.textContent = label;
      }
    }
    function scheduleRefresh() {
      if (!frame) frame = window.requestAnimationFrame(refresh);
    }
    function move(direction) {
      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = track.firstElementChild.getBoundingClientRect().width + gap;
      const left = direction === 'first' ? 0 : direction === 'last' ? maximum() : track.scrollLeft + direction * step;
      track.scrollTo({ left: Math.max(0, Math.min(maximum(), left)), behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('keydown', event => {
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const direction = { ArrowLeft: -1, ArrowRight: 1, Home: 'first', End: 'last' }[event.key];
      if (direction === undefined) return;
      event.preventDefault();
      move(direction);
    });
    track.addEventListener('scroll', scheduleRefresh, { passive: true });
    const observer = new ResizeObserver(scheduleRefresh);
    observer.observe(track);
    observer.observe(track.firstElementChild);
    track.campaignCarouselCleanup = () => { observer.disconnect(); window.cancelAnimationFrame(frame); };
    scheduleRefresh();
  }

  function tasteBoxFallbackDescription() {
    return "Se kassens vine og den samlede pris.";
  }

  function renderResourceStory(resource, campaign) {
    const story = element("aside", css + "resource-story");
    addText(story, "p", campaign.theme || "Udvalgt", css + "kicker");
    addText(story, "h3", campaign.content?.design === "story" || resource.type === "taste_box" ? resource.label : "Derfor er den udvalgt");
    const fallback = resource.type === "taste_box"
      ? tasteBoxFallbackDescription(resource)
      : "Et aktuelt valg fra sortimentet, klar til at gå videre med.";
    addText(story, "p", campaign.content?.design === "story" && text(campaign.content.long_copy)
      ? campaign.content.long_copy : resource.description || fallback, css + "body");
    appendLink(
      story,
      resource.type === "taste_box" ? "Se smagekassen" : "Se vinen",
      resource.url,
      css + "button button button--secondary"
    );
    return story;
  }

  function appendResourceIntro(wrapper, heading, campaign, root, includeAction = true) {
    const introImage = campaign.content?.design || bottleMode(campaign.content, campaign.resources)
      ? '' : text(campaign.content?.image_url);
    let copy = wrapper;
    if (introImage) {
      const intro = element("div", css + "resource-intro");
      const image = element("img");
      image.src = introImage;
      image.alt = "";
      image.loading = "lazy";
      intro.appendChild(image);
      copy = element("div", css + "resource-intro-copy");
      intro.appendChild(copy);
      wrapper.appendChild(intro);
    }
    if (text(heading)) addText(copy, "h2", heading, css + "section-heading title inline-richtext h2");
    addText(copy, "p", campaign.content?.subline, css + "resource-intro-subline");
    if (includeAction && text(root?.dataset.slot) !== 'forside-produkter' && campaign.content?.design !== 'story') {
      appendLink(copy, campaign.content?.cta_label, campaign.content?.link_target, css + "button button button--secondary");
    }
  }

  function appendResourceAction(wrapper, campaign) {
    const actions = element('div', css + 'resource-actions');
    const link = appendLink(
      actions,
      campaign.content?.cta_label,
      campaign.content?.link_target,
      css + 'button button button--secondary',
    );
    if (link) wrapper.appendChild(actions);
  }

  function cutoutBottleIds(resources, limit = 8) {
    const groups = new Map();
    bottleResources(resources)
      .filter((resource) => text(resource.neutral_image_url) && text(resource.url))
      .forEach((resource) => {
        const key = text(resource.taste_box_id) || 'product:' + text(resource.id);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(resource);
      });
    const selected = [];
    const values = [...groups.values()];
    for (let index = 0; selected.length < limit && values.some((group) => group[index]); index += 1) {
      values.forEach((group) => {
        if (selected.length < limit && group[index]) selected.push(text(group[index].id));
      });
    }
    return selected;
  }

  function renderTasteBoxFeature(resource, heading, root, campaign) {
    const wrapper = element("section", css + "resource-band " + css + "taste-box-feature page-width");
    if (text(root?.dataset.slot) === "forside-hero") wrapper.classList.add(css + "resource-band--hero");
    appendResourceIntro(wrapper, heading, campaign, root, false);
    const feature = element("article", css + "taste-box-feature-card");
    const mediaLink = element("a", css + "taste-box-feature-media");
    mediaLink.href = text(resource.url) || "#";
    if (text(resource.image_url)) {
      const image = element("img");
      prepareResourceImage(image, resource.image_url, "(max-width: 640px) 340px, 390px");
      image.alt = text(resource.label);
      mediaLink.appendChild(image);
    }
    feature.appendChild(mediaLink);

    const content = element("div", css + "taste-box-feature-content");
    const collectionKicker = element("p", css + "kicker");
    appendLink(collectionKicker, "Smagekasser", "/collections/smagekasser", css + "collection-link");
    content.appendChild(collectionKicker);
    const title = element("h3");
    appendLink(title, resource.label, resource.url, "full-unstyled-link");
    content.appendChild(title);
    addText(content, "p", campaign.content?.design === "story" && text(campaign.content.long_copy)
      ? campaign.content.long_copy : resource.description || tasteBoxFallbackDescription(resource), css + "body");
    const price = Number(resource.price);
    const compareAtPrice = Number(resource.compare_at_price);
    if (Number.isFinite(price)) {
      content.appendChild(renderProductPrice(
        price,
        compareAtPrice,
        resource.currency,
        resource.price_label,
        resource.id,
        resource.variant_id,
        root
      ));
    }
    appendLink(content, "Se smagekassen", resource.url, css + "button button button--secondary");
    feature.appendChild(content);
    wrapper.appendChild(feature);
    appendResourceAction(wrapper, campaign);
    return wrapper;
  }

  function renderResources(resources, heading, campaign, root) {
    if (!resources.length) {
      if (campaign.content?.design === 'story') return null;
      if (!campaign.content?.headline && !campaign.content?.image_url) return null;
      const empty = element("section", css + "resource-band page-width");
      appendResourceIntro(empty, heading, campaign, root);
      return empty;
    }
    const productDesign = ['products-rail', 'products-grid', 'products-cutout'].includes(campaign.content?.design);
    const featureSingleResource = resources.length === 1 && !productDesign
      && (text(root?.dataset.slot) !== "forside-produkter" || campaign.content?.design === "story");
    if (featureSingleResource && resources[0].type === "taste_box") {
      return renderTasteBoxFeature(resources[0], heading, root, campaign);
    }
    const productLayout = campaign.content?.design === "products-grid" ? "grid"
      : campaign.content?.design === "products-cutout" ? "cutout"
      : campaign.content?.design === "products-rail" ? "rail"
      : ['grid', 'spotlight'].includes(campaign.merchandising?.layout) ? campaign.merchandising.layout : 'rail';
    const wrapper = element("section", css + "resource-band page-width " + css + "resource-band--" + productLayout);
    if (text(root?.dataset.slot) === "forside-hero") wrapper.classList.add(css + "resource-band--hero");
    appendResourceIntro(wrapper, heading, campaign, root, !productDesign);
    const layout = element("div", css + "resource-layout" + (productLayout === 'cutout' ? ' ' + css + 'resource-layout--cutout' : ''));
    if (productLayout === 'cutout') {
      const lineup = renderWineLineup(resources, 'bottles-auto', false, cutoutBottleIds(resources), '', '', false);
      if (lineup) {
        lineup.classList.add(css + 'cutout-lineup');
        layout.appendChild(lineup);
        wrapper.appendChild(layout);
        appendResourceAction(wrapper, campaign);
        return wrapper;
      }
    }
    const grid = element("ul", css + "products");
    resources.forEach((resource) => {
      const cutout = productLayout === 'cutout' || resource.type === 'product' && (!campaign.content?.design
        && ['bottles', 'bottles-tilted', 'bottles-fan'].includes(campaign.content?.visual_mode));
      const productImage = cutout ? resource.neutral_image_url || resource.image_url : resource.image_url;
      const item = element("li", css + "product grid__item" + (cutout ? ' ' + css + 'product--cutout' : ''));
      const cardWrapper = element("div", "card-wrapper product-card-wrapper underline-links-hover");
      const card = element("div", "card card--standard card--media card--shape");
      const inner = element("div", "card__inner color-scheme-4 gradient ratio");
      inner.style.setProperty("--ratio-percent", "125%");
      if (text(productImage)) {
        const mediaWrapper = element("div", "card__media " + css + "product-media");
        const media = element("div", "media media--transparent media--hover-effect");
        const image = element("img");
        const desktopSize = root?.dataset.slot === "forside-produkter" ? "17vw" : "25vw";
        const sizes = featureSingleResource
          ? "(max-width: 640px) min(82vw, 360px), 420px"
          : `(max-width: 640px) min(72vw, 280px), (max-width: 989px) 33vw, ${desktopSize}`;
        prepareResourceImage(image, productImage, sizes);
        image.alt = text(resource.label);
        media.appendChild(image);
        mediaWrapper.appendChild(media);
        inner.appendChild(mediaWrapper);
      }
      card.appendChild(inner);

      const content = element("div", "card__content");
      const information = element("div", "card__information");
      const title = element("h3", "card__heading h5");
      if (resource.url) {
        const titleLink = element("a", "full-unstyled-link");
        titleLink.href = resource.url;
        titleLink.textContent = text(resource.label);
        title.appendChild(titleLink);
      } else {
        title.textContent = text(resource.label);
      }
      information.appendChild(title);
      if (resource.type === "product" && text(resource.vendor)) {
        addText(information, "span", "Producent", "visually-hidden");
        addText(information, "div", resource.vendor, "caption-with-letter-spacing light");
      }

      const price = Number(resource.price);
      const compareAtPrice = Number(resource.compare_at_price);
      if (Number.isFinite(price)) {
        information.appendChild(renderProductPrice(
          price,
          compareAtPrice,
          resource.currency,
          resource.price_label,
          resource.id,
          resource.variant_id,
          root
        ));
      }
      content.appendChild(information);

      const unavailable = resource.available_for_sale === false
        || (Number.isFinite(Number(resource.stock)) && Number(resource.stock) <= 0);
      if (unavailable || text(resource.variant_id)) {
        const quickAdd = element("div", "quick-add no-js-hidden");
        const productForm = element("product-form");
        const form = element("form", "form");
        form.method = "post";
        form.action = "/cart/add";
        form.noValidate = true;
        const variant = element("input");
        variant.type = "hidden";
        variant.name = "id";
        variant.value = text(resource.variant_id);
        variant.className = "product-variant-id";
        const button = element("button", "quick-add__submit button button--full-width button--secondary");
        button.type = "submit";
        button.name = "add";
        button.setAttribute("aria-live", "polite");
        button.setAttribute("data-sold-out-message", "true");
        const buttonLabel = element("span");
        buttonLabel.textContent = unavailable ? "Udsolgt" : "Læg i kurv";
        const soldOutMessage = element("span", "sold-out-message hidden");
        soldOutMessage.textContent = "Udsolgt";
        button.disabled = unavailable;
        button.append(buttonLabel, soldOutMessage, campaignCartLoadingSpinner(root));
        const errorWrapper = element("div", "product-form__error-message-wrapper");
        errorWrapper.setAttribute("role", "alert");
        errorWrapper.hidden = true;
        errorWrapper.appendChild(element("span", "product-form__error-message"));
        form.append(variant, button, errorWrapper);
        productForm.appendChild(form);
        quickAdd.appendChild(productForm);
        content.appendChild(quickAdd);
      }
      card.appendChild(content);
      cardWrapper.appendChild(card);
      item.appendChild(cardWrapper);
      grid.appendChild(item);
    });
    layout.appendChild(grid);
    if (featureSingleResource) {
      wrapper.classList.add(css + "resource-band--single");
      layout.appendChild(renderResourceStory(resources[0], campaign));
    } else if (productLayout === 'rail') {
      initProductCarousel(grid);
    }
    wrapper.appendChild(layout);
    if (productDesign) appendResourceAction(wrapper, campaign);
    return wrapper;
  }

  function liquidPriceOverride(root, productId, variantId) {
    if (!root) return null;
    const homepage = root.closest(".msk-campaign-homepage");
    if (!homepage) return null;
    const cleanProductId = text(productId).split("/").pop();
    const cleanVariantId = text(variantId).split("/").pop();
    const source = Array.from(homepage.querySelectorAll("template[data-msk-campaign-liquid-price]"))
      .find((candidate) => {
        return text(candidate.dataset.productId) === cleanProductId
          && (!cleanVariantId || text(candidate.dataset.variantId) === cleanVariantId);
      });
    const priceRoot = source?.content?.querySelector(".price");
    return priceRoot ? priceRoot.cloneNode(true) : null;
  }

  function renderProductPrice(price, compareAtPrice, currency, priceLabel, productId, variantId, root) {
    const liquidPrice = liquidPriceOverride(root, productId, variantId);
    if (liquidPrice) return liquidPrice;
    const onSale = Number.isFinite(compareAtPrice) && compareAtPrice > price;
    const priceRoot = element("div", "price" + (onSale ? " price--on-sale" : ""));
    const memberPrice = text(priceLabel) === "Din pris";
    priceRoot.dataset.mskPriceContext = memberPrice ? "member" : "standard";
    priceRoot.dataset.mskProductId = text(productId);
    priceRoot.dataset.mskVariantId = text(variantId);
    priceRoot.dataset.mskPriceCents = String(Math.round(price * 100));
    priceRoot.dataset.mskCompareAtPriceCents = Number.isFinite(compareAtPrice)
      ? String(Math.round(compareAtPrice * 100))
      : "";
    const container = element("div", "price__container");
    const regular = element("div", "price__regular");
    if (memberPrice) addText(regular, "span", priceLabel, "price__member-label");
    addText(regular, "span", formatMoney(onSale ? compareAtPrice : price, currency), "price-item price-item--regular");
    container.appendChild(regular);
    if (onSale) {
      const sale = element("div", "price__sale");
      addText(sale, "s", formatMoney(compareAtPrice, currency), "price-item price-item--regular");
      if (memberPrice) {
        const memberValue = element("span", "price__member-value");
        addText(memberValue, "span", priceLabel, "price__member-label");
        addText(memberValue, "span", formatMoney(price, currency), "price-item price-item--sale price-item--last");
        sale.appendChild(memberValue);
      } else {
        addText(sale, "span", formatMoney(price, currency), "price-item price-item--sale price-item--last");
      }
      container.appendChild(sale);
    }
    priceRoot.appendChild(container);
    return priceRoot;
  }

  function formatMoney(value, currency) {
    try {
      const number = Number(value);
      return new Intl.NumberFormat("da-DK", {
        style: "currency",
        currency: text(currency) || "DKK",
        minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(number);
    } catch {
      return Number(value).toLocaleString("da-DK") + " kr.";
    }
  }

  function renderCopySection(item, className, options) {
    if (!item) return null;
    return renderResponsiveArtwork(item, content => renderCopyArtwork(content, className, options));
  }

  function renderCopyArtwork(item, className, options) {
    const hasButton = Boolean(text(item.cta_label) && text(item.link_target));
    if (![item.section_type, item.title, item.copy, item.image_url, item.mobile_image_url].some(text) && !hasButton) return null;
    const settings = options || {};
    const section = element("section", className || css + "copy");
    if (item.visual_mode === 'title-button') {
      section.classList.add(css + 'title-button');
      if (!text(item.image_url || item.mobile_image_url)) section.classList.add(css + 'title-button--plain');
    }
    setResponsiveImageCrop(section, item);
    const mode = bottleMode(item, settings.resources);
    const imageUrl = text(item.image_url || item.mobile_image_url);
    const sidePhoto = settings.backdrop && [item.banner_layout, item.mobile_banner_layout].some(value => bannerLayout(value).image === 'right');
    if (imageUrl && mode) {
      section.classList.add(css + 'bottle-feature--backdrop');
      setResponsiveBackdrop(section, item);
      appendPhotoCredit(section, item);
    } else if (imageUrl && settings.backdrop && !sidePhoto) {
      section.classList.add(css + "focus-panel--backdrop");
      setResponsiveBackdrop(section, item);
      appendPhotoCredit(section, item);
    } else if (imageUrl) {
      section.classList.add(css + (sidePhoto ? 'photo-banner' : 'copy--with-image'));
      const image = element("img", sidePhoto ? '' : css + "copy-image");
      image.src = imageUrl;
      image.alt = "";
      image.loading = "lazy";
      const picture = element('picture', css + 'copy-picture');
      if (text(item.mobile_image_url)) addMobileSource(picture, item.mobile_image_url);
      picture.appendChild(image);
      if (sidePhoto) {
        section.appendChild(picture);
        appendPhotoCredit(section, item);
      } else {
        const media = element('div', css + 'copy-media');
        media.appendChild(picture);
        appendPhotoCredit(media, item);
        section.appendChild(media);
      }
    }
    const content = element("div", css + "copy-content");
    addText(content, "p", item.section_type, css + "kicker");
    addText(content, "h3", item.title);
    addText(content, "div", item.copy, css + "body");
    appendLink(content, item.cta_label, item.link_target, css + "button button button--secondary");
    if (content.childElementCount) section.appendChild(content);
    if (mode) {
      section.classList.add(css + 'bottle-feature');
      const lineup = renderWineLineup(settings.resources, mode, false, item.image_product_ids, item.image_taste_box_id, item.image_caption, true, item.image_bottle_repeat);
      if (lineup) section.appendChild(lineup);
    }
    applyBannerLayout(section, item);
    return section;
  }

  function renderFocus(sections, root, display, resources = []) {
    const focus = text(root.dataset.focus) || "rotate";
    const focusOptions = { backdrop: true, resources };
    const rotationEnabled = Boolean(display && display.section_rotation_enabled);
    if (focus === 'rotate' && rotationEnabled && display?.section_layout === 'tiles' && sections.length > 1) {
      const tiles = element('section', css + 'focus-tiles');
      sections.map(item => renderCopySection(item, css + 'focus-panel', focusOptions))
        .filter(Boolean).forEach(panel => tiles.appendChild(panel));
      return tiles;
    }
    if (focus !== "rotate" || !rotationEnabled || sections.length < 2) {
      return renderCopySection(
        sections[clamp(focus, 1, 1, 5) - 1],
        css + "copy " + css + "copy--focus",
        focusOptions
      );
    }

    const panels = sections.map((item) => {
      return renderCopySection(item, css + "focus-panel", focusOptions);
    }).filter(Boolean);
    if (!panels.length) return null;

    return createRotator(panels, {
      className: css + "focus",
      trackClassName: css + "focus-track",
      controlsClassName: css + "focus-controls",
      itemLabel: "fokus",
      autoplay: rotationEnabled && root.dataset.autoplay !== "false",
      intervalSeconds: display && display.section_rotation_seconds,
      fallbackSeconds: 8,
      minimumSeconds: 3,
      maximumSeconds: 60,
    });
  }

  function renderOffers(offers, heading) {
    if (!offers.length) return null;
    const section = element("section", css + "offer-band");
    addText(section, "h2", heading, css + "section-heading");
    const grid = element("div", css + "offers");
    offers.forEach((offer) => {
      const item = element("div");
      addText(item, "span", "Tilbud");
      addText(item, "strong", offer.name);
      addText(item, "p", offer.rule);
      addText(item, "small", offer.customer_value);
      grid.appendChild(item);
    });
    section.appendChild(grid);
    return section;
  }

  function endpointForBlocks(container, roots) {
    const endpoint = text(roots[0] && roots[0].dataset.endpoint) || "/apps/kundeprofil/campaign/active.json";
    const url = new URL(endpoint, window.location.origin);
    const slots = roots.map((root) => text(root.dataset.slot)).filter(Boolean);
    const companyLocationId = text(container.dataset.companyLocationId);
    if (slots.length) url.searchParams.set("slots", slots.join(","));
    if (companyLocationId) url.searchParams.set("company_location_id", companyLocationId);
    return url.toString();
  }

  function homepageContextKey(container) {
    return [
      text(container.dataset.customerId) || "anonymous",
      text(container.dataset.companyLocationId) || "standard",
    ].join(":");
  }

  function homepageRequestKey(container, endpoint) {
    return homepageContextKey(container) + ":" + endpoint;
  }

  function campaignPriorityImageUrls(campaign) {
    if (!campaign) return [];
    const firstSlide = Array.isArray(campaign.hero_slides) ? campaign.hero_slides[0] : null;
    const hero = campaign.hero?.image_url || campaign.hero?.mobile_image_url ? campaign.hero : firstSlide || {};
    const mobile = window.matchMedia?.('(max-width: 640px)').matches;
    return [mobile && hero.mobile_image_url || hero.image_url || hero.mobile_image_url].map(text).filter(Boolean);
  }

  function preloadHomepageImages(payload) {
    const campaigns = payload && payload.campaigns ? Object.values(payload.campaigns) : [];
    [...new Set(campaigns.flatMap(campaignPriorityImageUrls))].forEach((url) => {
      const image = new Image();
      prepareHeroImage(image, url);
    });
  }

  function requestHomepage(container, endpoint) {
    const requestKey = homepageRequestKey(container, endpoint);
    if (!requests.has(requestKey)) {
      const earlyRequest = window.__MskCampaignHomepageRequests
        && window.__MskCampaignHomepageRequests[requestKey];
      requests.set(requestKey, Promise.resolve(earlyRequest || window.fetch(endpoint, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
      }).then((response) => response.ok ? response.json() : null))
        .then((payload) => payload && payload.campaigns ? payload : null)
        .then((payload) => {
          if (payload) {
            preloadHomepageImages(payload);
          }
          return payload;
        })
        .catch(() => null));
    }
    return requests.get(requestKey);
  }

  function campaignResources(campaign, root) {
    const flexibleField = campaign.kind === "evergreen" && text(campaign.display?.homepage_content_mode) === "products";
    const design = text(campaign.content?.design);
    const requestedType = flexibleField || design ? "all" : text(root.dataset.resourceType) || "all";
    const limit = flexibleField
      ? clamp(campaign.merchandising?.limit, 6, 1, 12)
      : clamp(root.dataset.limit, 6, 1, 12);
    const featuredId = design === "story" ? text(campaign.content?.featured_product_id) : "";
    return (Array.isArray(campaign.resources) ? campaign.resources : [])
      .filter((resource) => {
        return resource && (requestedType === "all" || resource.type === requestedType)
          && (design !== "story" || featuredId && text(resource.id) === featuredId);
      })
      .slice(0, design === "story" ? 1 : limit);
  }

  function campaignItems(campaign, key) {
    return (Array.isArray(campaign[key]) ? campaign[key] : []).filter(Boolean);
  }

  function campaignOffers(campaign, root) {
    const limit = clamp(root.dataset.limit, 3, 1, 3);
    return campaignItems(campaign, "offers").slice(0, limit);
  }

  function appendRendered(root, node) {
    if (!node) return false;
    root.appendChild(node);
    [node, ...node.querySelectorAll('.' + css + 'banner-layout')]
      .forEach((section) => section.campaignBannerRelayout?.());
    return true;
  }

  function popupStorageKey(campaign) {
    return `msk-campaign-popup:${text(campaign?.id)}`;
  }

  function popupWasShown(campaign) {
    try {
      const shownAt = Number(window.localStorage.getItem(popupStorageKey(campaign)) || 0);
      const frequency = clamp(campaign?.action?.popup?.frequency_days, 14, 1, 90) * 86400000;
      return shownAt > 0 && Date.now() - shownAt < frequency;
    } catch {
      return false;
    }
  }

  function rememberPopup(campaign) {
    try { window.localStorage.setItem(popupStorageKey(campaign), String(Date.now())); } catch { /* Storage can be blocked. */ }
  }

  function hiddenFormInput(name, value) {
    const input = element("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    return input;
  }

  function renderPopup(campaign, root) {
    const action = campaign?.action || {};
    if (!action.popup?.enabled || !text(action.headline)) return null;
    const layer = element("div", css + "popup-layer");
    layer.hidden = true;
    const dialog = element("section", css + "popup");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("aria-labelledby", `campaign-popup-title-${text(campaign.id)}`);
    const close = element("button", css + "popup-close");
    close.type = "button";
    close.setAttribute("aria-label", "Luk");
    close.textContent = "×";
    const title = addText(dialog, "h2", action.headline);
    if (title) title.id = `campaign-popup-title-${text(campaign.id)}`;
    addText(dialog, "p", action.body, css + "popup-copy");
    dialog.prepend(close);

    if (campaign.objective === "newsletter_signup") {
      const form = element("form", css + "popup-form");
      form.method = "post";
      form.action = "/contact#contact_form";
      form.acceptCharset = "UTF-8";
      form.append(hiddenFormInput("form_type", "customer"), hiddenFormInput("utf8", "✓"), hiddenFormInput("contact[tags]", "newsletter"));
      const label = element("label");
      const labelText = addText(label, "span", "Din e-mailadresse");
      if (labelText) labelText.className = "visually-hidden";
      const email = element("input");
      email.type = "email";
      email.name = "contact[email]";
      email.autocomplete = "email";
      email.placeholder = "Din e-mailadresse";
      email.required = true;
      const submit = element("button", "button button--primary");
      submit.type = "submit";
      submit.textContent = text(action.cta_label) || "Tilmeld mig";
      label.appendChild(email);
      form.append(label, submit);
      dialog.appendChild(form);
      addText(dialog, "p", "Du kan altid afmelde dig igen.", css + "popup-note");
    } else {
      appendLink(dialog, action.cta_label, action.cta_url, css + "button button button--primary");
    }
    layer.appendChild(dialog);

    let timer = 0;
    let open = false;
    let previousFocus = null;
    let previousOverflow = "";
    const onKeydown = event => {
      if (event.key === "Escape") {
        closePopup();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const closePopup = () => {
      if (!open) return;
      open = false;
      layer.hidden = true;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeydown);
      previousFocus?.focus?.({ preventScroll: true });
    };
    const showPopup = () => {
      if (open || !layer.isConnected) return;
      open = true;
      previousFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      layer.hidden = false;
      rememberPopup(campaign);
      document.addEventListener("keydown", onKeydown);
      window.requestAnimationFrame(() => (dialog.querySelector('input[type="email"]')
        || dialog.querySelector('a, button'))?.focus?.({ preventScroll: true }));
    };
    close.addEventListener("click", closePopup);
    layer.addEventListener("click", event => { if (event.target === layer) closePopup(); });

    const forced = campaign.objective === "newsletter_signup"
      && new URLSearchParams(window.location.search).get("campaign") === "newsletter";
    const schedule = action.popup || {};
    const onScroll = () => {
      const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      if (window.scrollY / scrollable * 100 < clamp(schedule.scroll_percent, 35, 10, 90)) return;
      window.removeEventListener("scroll", onScroll);
      showPopup();
    };
    if (forced || !popupWasShown(campaign)) {
      if (forced) timer = window.setTimeout(showPopup, 0);
      else if (schedule.trigger === "scroll") window.addEventListener("scroll", onScroll, { passive: true });
      else timer = window.setTimeout(showPopup, clamp(schedule.delay_seconds, 5, 0, 60) * 1000);
    }
    root.campaignPopupCleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      closePopup();
    };
    return layer;
  }

  function homepageBlockMode(root, campaign) {
    const design = text(campaign?.content?.design);
    if (design === "banner") return ["forside-fokus", "forside-kampagne"].includes(text(root?.dataset.slot)) ? "focus" : "hero";
    if (["products-rail", "products-grid", "products-cutout", "story"].includes(design)) return "products";
    if (campaign?.kind === "evergreen") {
      const chosen = text(campaign.display?.homepage_content_mode);
      if (chosen === "banner") return "hero";
      if (chosen === "products") return "products";
      if (chosen === "sections") return "focus";
    }
    return text(root?.dataset.mode) || "all";
  }

  function renderCampaign(root, campaign) {
    const mode = homepageBlockMode(root, campaign);
    cleanupCampaignLayouts(root);
    root.replaceChildren();
    let rendered = false;

    if (mode === "popup") {
      rendered = appendRendered(root, renderPopup(campaign, root));
    } else if (mode === "hero") {
      rendered = appendRendered(root, renderHero(campaign));
    } else if (mode === "products") {
      const heading = campaign.kind === "evergreen" && campaign.display?.homepage_content_mode === "products"
        ? campaign.display.homepage_heading
        : campaign.content?.headline || root.dataset.heading;
      rendered = appendRendered(root, renderResources(campaignResources(campaign, root), heading, campaign, root));
    } else if (mode === "focus") {
      rendered = appendRendered(root, renderFocus(campaignItems(campaign, "sections"), root, campaign.display || {}, campaign.resources));
    } else if (mode === "theme") {
      const sections = campaignItems(campaign, "sections");
      rendered = appendRendered(root, sections.length
        ? renderFocus(sections, root, campaign.display || {}, campaign.resources)
        : renderHero(campaign));
    } else if (mode === "offers") {
      rendered = appendRendered(root, renderOffers(campaignOffers(campaign, root), root.dataset.heading));
    } else {
      rendered = appendRendered(root, renderHero(campaign)) || rendered;
      if (root.dataset.showProducts !== "false") {
        rendered = appendRendered(root, renderResources(campaignResources(campaign, root), "", campaign, root)) || rendered;
      }
      campaignItems(campaign, "sections").forEach((item) => {
        rendered = appendRendered(root, renderCopySection(item)) || rendered;
      });
      if (root.dataset.showOffers !== "false") {
        rendered = appendRendered(root, renderOffers(campaignOffers(campaign, root), "")) || rendered;
      }
    }
    root.hidden = !rendered;
    root.setAttribute("aria-busy", "false");
  }

  function renderHomepage(container, payload, roots) {
    const campaigns = payload && payload.campaigns ? payload.campaigns : {};
    const productRoot = roots.find((root) => text(root.dataset.slot) === "forside-produkter");
    const focusRoot = roots.find((root) => text(root.dataset.slot) === "forside-fokus");
    const featuredTasteBox = productRoot && homepageBlockMode(productRoot, campaigns["forside-produkter"]) === "products"
      && campaignItems(campaigns["forside-produkter"] || {}, "resources")
      .some((resource) => resource && resource.type === "taste_box");
    roots.forEach((root) => {
      const slot = text(root.dataset.slot);
      const sourceCampaign = campaigns[slot];
      const campaign = slot === "forside-fokus" && featuredTasteBox && focusRoot
        && homepageBlockMode(focusRoot, sourceCampaign) === "focus" && sourceCampaign
        ? {
            ...sourceCampaign,
            sections: campaignItems(sourceCampaign, "sections").filter((section) => {
              const values = [section.section_type, section.title, section.link_target].map(text).join(" ");
              return !/smagekass/i.test(values);
            }),
          }
        : sourceCampaign;
      if (campaign) {
        renderCampaign(root, campaign);
        return;
      }
      const fallback = root.querySelector("[data-msk-campaign-fallback]");
      if (fallback) {
        root.hidden = false;
        root.setAttribute("aria-busy", "false");
        return;
      }
      cleanupCampaignLayouts(root);
      root.replaceChildren();
      root.hidden = true;
      root.setAttribute("aria-busy", "false");
    });
  }

  async function initHomepageBatch(container, roots) {
    if (!roots.length) return;
    const endpoint = endpointForBlocks(container, roots);
    const payload = await requestHomepage(container, endpoint);
    if (payload) renderHomepage(container, payload, roots);
    else renderHomepage(container, null, roots);
  }

  function initHomepage(container) {
    const contextKey = homepageContextKey(container);
    if (container.dataset.campaignInitialized === contextKey) return;
    container.dataset.campaignInitialized = contextKey;
    const roots = Array.from(container.querySelectorAll("[data-msk-campaign-block]"));
    const heroRoots = roots.filter((root) => text(root.dataset.slot) === "forside-hero");
    const remainingRoots = roots.filter((root) => text(root.dataset.slot) !== "forside-hero");
    void initHomepageBatch(container, heroRoots);
    void initHomepageBatch(container, remainingRoots);
  }

  function initAll(scope) {
    const container = scope && typeof scope.querySelectorAll === "function" ? scope : document;
    const homepages = Array.from(container.querySelectorAll(".msk-campaign-homepage"));
    if (container.matches && container.matches(".msk-campaign-homepage")) homepages.unshift(container);
    homepages.forEach(initHomepage);
  }

  function cleanupCampaignLayouts(root) {
    const campaignRoots = [root, ...Array.from(root.querySelectorAll?.("[data-msk-campaign-block]") || [])];
    campaignRoots.forEach((campaignRoot) => {
      campaignRoot.campaignPopupCleanup?.();
      campaignRoot.campaignPopupCleanup = null;
    });
    root.querySelectorAll('.' + css + 'products--carousel').forEach(track => track.campaignCarouselCleanup?.());
    root.querySelectorAll('.' + css + 'wine-lineup').forEach(lineup => lineup.campaignLineupCleanup?.());
    root.querySelectorAll('.' + css + 'banner-layout').forEach(banner => banner.campaignBannerCleanup?.());
  }

  window.MskCampaignBlocks = { initAll: initAll, render: renderCampaign, setImageCrop: setResponsiveImageCrop };
  initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => cleanupCampaignLayouts(event.target));
})();
